/**
 * AppointmentsDashboard — FASE 2: KPIs, gráfico, sesiones de hoy
 * Referencia: 0257-Maqueta-Desconfundir-Usuario-Agenda
 */
import React, { useMemo, useEffect } from "react";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";
import "moment/locale/en-gb";
import { useTheme } from "@material-ui/core/styles";
import { useStyles } from "./styles";
import { statusColor, softTone, withAlpha } from "../../theme/agendaTheme";
import { i18n } from "../../translate/i18n";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function AppointmentsDashboard({ appointments }) {
  const classes = useStyles();
  const theme = useTheme();
  const agendaMode = (theme.agenda && theme.agenda.mode) || theme.palette.type;
  const agendaPalette = (theme.agenda && theme.agenda.palette) || {};

  useEffect(() => {
    const lang = (i18n.language || "es").split("-")[0];
    if (lang === "en") moment.locale("en-gb");
    else if (lang === "pt") moment.locale("pt");
    else moment.locale("es");
  }, [i18n.language]);

  const weekdayLabels = useMemo(
    () => [0, 1, 2, 3, 4, 5, 6].map((i) => i18n.t(`appointmentsModule.dashboard.wd${i}`)),
    [i18n.language],
  );

  const kpis = useMemo(() => {
    const today = moment().startOf("day");
    const weekStart = moment().startOf("week");
    const weekEnd = moment().endOf("week");
    const confirmed = appointments.filter((a) => a.status === "confirmed" || a.status === "completed");
    const pending = appointments.filter((a) => a.status === "pending");
    const todaySessions = appointments.filter((a) => moment(a.appointmentDate).isSame(today, "day"));
    const weekSessions = appointments.filter((a) =>
      moment(a.appointmentDate).isBetween(weekStart, weekEnd, null, "[]")
    );

    return {
      semana: weekSessions.length,
      pendientes: pending.length,
      hoy: todaySessions.length,
      tasa: appointments.length > 0
        ? Math.round((confirmed.length / appointments.length) * 100)
        : 0
    };
  }, [appointments]);

  const chartData = useMemo(() => {
    const weekStart = moment().startOf("week");
    const conf = [0, 0, 0, 0, 0, 0, 0];
    const pend = [0, 0, 0, 0, 0, 0, 0];
    const canc = [0, 0, 0, 0, 0, 0, 0];
    appointments.forEach((a) => {
      const d = moment(a.appointmentDate);
      if (d.isSame(weekStart, "week")) {
        const idx = d.day() === 0 ? 6 : d.day() - 1;
        if (a.status === "confirmed" || a.status === "completed") conf[idx]++;
        else if (a.status === "pending") pend[idx]++;
        else if (a.status === "cancelled") canc[idx]++;
      }
    });

    return {
      labels: weekdayLabels,
      datasets: [
        { label: i18n.t("appointmentsModule.dashboard.chartConfirmed"), data: conf, backgroundColor: withAlpha(statusColor("confirmed"), 0.7) },
        { label: i18n.t("appointmentsModule.dashboard.chartPending"), data: pend, backgroundColor: withAlpha(statusColor("pending"), 0.7) },
        { label: i18n.t("appointmentsModule.dashboard.chartCancelled"), data: canc, backgroundColor: withAlpha(statusColor("cancelled"), 0.7) }
      ]
    };
  }, [appointments, weekdayLabels, i18n.language]);

  // Chart.js pinta ejes y leyenda en #666 por defecto: ilegible sobre superficie oscura.
  const chartOptions = useMemo(() => {
    const tick = agendaPalette.textSecondary;
    const grid = agendaPalette.border;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top", labels: { color: tick } },
        tooltip: {
          backgroundColor: agendaPalette.overlay,
          titleColor: agendaPalette.textPrimary,
          bodyColor: agendaPalette.textPrimary,
          borderColor: grid,
          borderWidth: 1,
        },
      },
      scales: {
        x: { ticks: { color: tick }, grid: { color: grid } },
        y: { beginAtZero: true, ticks: { color: tick }, grid: { color: grid } },
      },
    };
  }, [agendaPalette]);

  const todaySessions = useMemo(() => {
    const today = moment().startOf("day");
    return appointments
      .filter((a) => moment(a.appointmentDate).isSame(today, "day"))
      .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));
  }, [appointments]);

  const proximasSessions = useMemo(() => {
    const tomorrow = moment().add(1, "day").startOf("day");
    const in30Days = moment().add(30, "days").endOf("day");
    return appointments
      .filter((a) => {
        const d = moment(a.appointmentDate);
        return d.isAfter(tomorrow) || d.isSame(tomorrow, "day");
      })
      .filter((a) => moment(a.appointmentDate).isBefore(in30Days) || moment(a.appointmentDate).isSame(in30Days, "day"))
      .filter((a) => a.status !== "cancelled" && a.status !== "canceled")
      .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate))
      .slice(0, 10);
  }, [appointments]);

  const formatTime = (dt) => (dt ? moment(dt).format("HH:mm") : "-");
  const formatDate = (dt) => (dt ? moment(dt).format("ddd D MMM") : "-");

  const getStatusLabel = (status) => {
    const k = ["pending", "confirmed", "completed", "rescheduled", "in_progress", "cancelled", "no_show"].includes(status)
      ? status
      : null;
    return k ? i18n.t(`appointmentsModule.status.${k}`) : status;
  };

  // Fuente única de color de estado (antes: tercer mapa local divergente)
  const statusPillStyle = (status) => softTone(statusColor(status), agendaMode);

  const chartMonthTitle = useMemo(
    () => i18n.t("appointmentsModule.dashboard.chartTitle", { month: moment().format("MMMM YYYY") }),
    [i18n.language],
  );

  return (
    <div style={{ padding: 24 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md>
          <Paper className={classes.dashboardKpi} elevation={0}>
            <Typography variant="h4">{kpis.semana}</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("appointmentsModule.dashboard.kpiWeek")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Paper className={classes.dashboardKpi} elevation={0}>
            <Typography variant="h4">{kpis.pendientes}</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("appointmentsModule.dashboard.kpiPending")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Paper className={classes.dashboardKpi} elevation={0}>
            <Typography variant="h4">{kpis.hoy}</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("appointmentsModule.dashboard.kpiToday")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Paper className={classes.dashboardKpi} elevation={0}>
            <Typography variant="h4">{kpis.tasa}%</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("appointmentsModule.dashboard.kpiRate")}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      <Paper className={classes.dashboardChart} elevation={0}>
        <Typography variant="h6" gutterBottom>
          {chartMonthTitle}
        </Typography>
        <div style={{ height: 200 }}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      </Paper>
      <Paper className={classes.dashboardToday} elevation={0}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("appointmentsModule.dashboard.todayTitle")}
        </Typography>
        {todaySessions.length === 0 ? (
          <Typography variant="body2" color="textSecondary">{i18n.t("appointmentsModule.dashboard.todayEmpty")}</Typography>
        ) : (
          todaySessions.map((a) => (
            <div key={a.id} className={classes.dashboardTodayItem}>
              <Typography variant="body1">
                {i18n.t("appointmentsModule.dashboard.todayLine", {
                  time: formatTime(a.appointmentDate),
                  contact: a.contactName || i18n.t("appointmentsModule.calendar.noName"),
                  service: a.serviceName || i18n.t("appointmentsModule.calendar.serviceFallback"),
                })}
              </Typography>
              {a.professionalName && (
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("appointmentsModule.dashboard.professionalPrefix")} {a.professionalName}
                </Typography>
              )}
            </div>
          ))
        )}
      </Paper>
      <Paper className={classes.dashboardToday} elevation={0} style={{ marginTop: 16 }}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("appointmentsModule.dashboard.upcomingTitle")}
        </Typography>
        {proximasSessions.length === 0 ? (
          <Typography variant="body2" color="textSecondary">{i18n.t("appointmentsModule.dashboard.upcomingEmpty")}</Typography>
        ) : (
          proximasSessions.map((a) => (
            <div key={a.id} className={classes.dashboardTodayItem} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div>
                <Typography variant="body1">
                  {formatDate(a.appointmentDate)} {formatTime(a.appointmentDate)} — {a.contactName || i18n.t("appointmentsModule.calendar.noName")} — {a.serviceName || i18n.t("appointmentsModule.calendar.serviceFallback")}
                </Typography>
                {a.professionalName && (
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("appointmentsModule.dashboard.professionalPrefix")} {a.professionalName}
                  </Typography>
                )}
              </div>
              <span className={classes.statusPill} style={statusPillStyle(a.status)}>
                {getStatusLabel(a.status)}
              </span>
            </div>
          ))
        )}
      </Paper>
    </div>
  );
}
